import PropTypes from 'prop-types';
import React from 'react';
import compact from 'lodash/compact';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { withRouter } from '@bibliocommons/utils-routing';
import AnalyticsConstants from '@bibliocommons/constants-analytics';
import { catalogBibShape, listShape, recommendationShape, routerShape } from '@bibliocommons/bc-prop-types';

import Context from '@bibliocommons/context';

import RedirectActions from 'app/actions/RedirectActions';
import { selectCatalogBib } from 'app/selectors/catalogBibs/CatalogBibSelector';
import { selectLinkedDataForCurrentBib } from 'app/selectors/bibs/LinkedDataSelector';
import { selectAnalyticsContextForCurrentBib } from 'app/selectors/bibs/AnalyticsSelector';
import { selectTagSummariesForCurrentBib } from 'app/selectors/bibs/UgcSelector';
import { selectPreviewFeatures } from 'app/selectors/PreviewSelector';
import {
  selectStaffListsForCurrentBib,
  selectRecommendationsForCurrentBib,
  selectRelatedTitlesForCurrentBib,
  selectAuthorSuggestionsForCurrentBib
} from 'app/selectors/bibs/DiscoverySelector';
import {
  selectRelatedAuthorsForCurrentBib,
  selectOnlineResourcesForCurrentBib
} from 'app/selectors/bibs/EvaluationSelector';

import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { AnalyticsContextContainer } from 'app/components/analytics/AnalyticsContext';
import NotFoundPage from 'app/components/NotFoundPage/NotFoundPage';
import BibPage from './BibPage';

export class BibPageContainer extends React.PureComponent {
  renderDocumentHead() {
    const { catalogBib, linkedData } = this.props;
    const pageTitle = catalogBib.getIn(['brief', 'title']);
    const imageUrl = catalogBib.getIn(['brief', 'coverImage', 'large']);
    const authorName = catalogBib.getIn(['brief', 'creators', 0, 'fullName']);
    const description = compact([pageTitle, authorName, catalogBib.getIn(['brief', 'description'])]).join(' — ');
    const pageUrl = `${this.context.baseURL}/v2/record/${catalogBib.get('metadataId')}`;
    const noIndex = catalogBib.get('localizationLevel') === 'UNMATCHED';

    return (
      <DocumentHeadContainer
        pageTitle={pageTitle}
        description={description}
        imageUrl={imageUrl}
        linkedData={linkedData}
        pageUrl={pageUrl}
        noIndex={noIndex}
      />
    );
  }

  render() {
    const {
      catalogBib,
      staffLists,
      recommendations,
      tags,
      relatedTitles,
      authorSuggestions,
      relatedAuthors,
      analyticsContext,
      onlineResources
    } = this.props;

    if (catalogBib) {
      const displayProps = {
        staffLists,
        recommendations,
        relatedTitles,
        tags,
        authorSuggestions,
        relatedAuthors,
        onlineResources
      };

      return (
        <div>
          {this.renderDocumentHead()}
          <AnalyticsContextContainer context={analyticsContext} contextName={AnalyticsConstants.CONTEXTS.BIB} />
          <BibPage catalogBib={this.props.catalogBib} {...displayProps} />
        </div>
      );
    }

    return <NotFoundPage />;
  }
}

BibPageContainer.propTypes = {
  catalogBib: catalogBibShape,
  linkedData: ImmutablePropTypes.map.isRequired,
  previewFeatures: ImmutablePropTypes.map.isRequired,
  analyticsContext: ImmutablePropTypes.map.isRequired,
  redirectActions: PropTypes.objectOf(PropTypes.func).isRequired,
  router: routerShape.isRequired,

  // Props below are used to conditionally render section titles and dividers
  staffLists: ImmutablePropTypes.listOf(listShape).isRequired,
  relatedTitles: ImmutablePropTypes.listOf(catalogBibShape).isRequired,
  recommendations: ImmutablePropTypes.listOf(recommendationShape).isRequired,
  tags: ImmutablePropTypes.list.isRequired,
  authorSuggestions: ImmutablePropTypes.list.isRequired,
  relatedAuthors: ImmutablePropTypes.list.isRequired,
  onlineResources: ImmutablePropTypes.list.isRequired
};

BibPageContainer.contextType = Context;

const mapStateToProps = state => ({
  catalogBib: selectCatalogBib(state),
  linkedData: selectLinkedDataForCurrentBib(state),
  analyticsContext: selectAnalyticsContextForCurrentBib(state),
  previewFeatures: selectPreviewFeatures(state),

  // Props below are used to conditionally render section titles and dividers
  staffLists: selectStaffListsForCurrentBib(state),
  relatedTitles: selectRelatedTitlesForCurrentBib(state),
  recommendations: selectRecommendationsForCurrentBib(state),
  tags: selectTagSummariesForCurrentBib(state),
  authorSuggestions: selectAuthorSuggestionsForCurrentBib(state),
  relatedAuthors: selectRelatedAuthorsForCurrentBib(state),
  onlineResources: selectOnlineResourcesForCurrentBib(state)
});

const mapDispatchToProps = dispatch => ({
  redirectActions: bindActionCreators(RedirectActions, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(BibPageContainer)));
